// Ventana "Novedades de epms": aparece al entrar si hay versiones que el
// usuario no vio, o cuando la abre desde su menú. "Entendido" la marca como
// vista (en el servidor: no se repite en otra computadora).
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Button, Divider, Group, Modal, ScrollArea, Stack, Text, ThemeIcon } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { IconSparkles } from '@tabler/icons-react';
import { api } from '../api.ts';
import { formatearFecha } from '../formato.ts';
import { ULTIMA_VERSION, VERSIONES, versionesSinVer, type Version } from '../novedades.ts';
import { RUTAS } from '../rutas.ts';
import { CLAVE_YO, usePerfil } from '../sesion.ts';

export function useNovedades() {
  const { data: perfil } = usePerfil();
  const qc = useQueryClient();
  const sinVer = perfil ? versionesSinVer(perfil.novedadesVistas) : [];
  const marcarVistas = async () => {
    await api.post('/api/auth/novedades', { hasta: ULTIMA_VERSION }).catch(() => {});
    await qc.invalidateQueries({ queryKey: CLAVE_YO });
  };
  return { sinVer, marcarVistas };
}

/** `todas`: abierta a mano desde el menú (muestra las últimas aunque ya las haya visto). */
export function VentanaNovedades({ todas, alCerrar }: { todas?: boolean; alCerrar?: () => void }) {
  const navegar = useNavigate();
  const { sinVer, marcarVistas } = useNovedades();
  const [cerrada, setCerrada] = useState(false);
  const versiones: Version[] = todas ? VERSIONES.slice(0, 3) : sinVer;
  if (cerrada || !versiones.length) return null;

  const cerrar = () => {
    setCerrada(true);
    void marcarVistas();
    alCerrar?.();
  };
  const ir = (ruta: string) => { cerrar(); navegar(ruta); };

  return (
    <Modal opened onClose={cerrar} size="lg" centered
      title={<Group gap="xs"><ThemeIcon variant="light" color="grape" radius="xl"><IconSparkles size={16} /></ThemeIcon><Text fw={700}>Novedades de epms</Text></Group>}>
      <ScrollArea.Autosize mah="60vh">
        <Stack gap="md">
          {versiones.map((v, i) => (
            <Stack key={v.fecha} gap="sm">
              {i > 0 && <Divider />}
              <Badge variant="light" color="grape" w="fit-content">Versión del {formatearFecha(v.fecha)}</Badge>
              {v.novedades.map((n) => (
                <div key={n.titulo}>
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Text fw={600} size="sm">{n.titulo}</Text>
                    {n.ir && <Button size="compact-xs" variant="subtle" onClick={() => ir(n.ir!)}>Ir</Button>}
                  </Group>
                  <Text size="sm" c="dimmed">{n.texto}</Text>
                </div>
              ))}
            </Stack>
          ))}
        </Stack>
      </ScrollArea.Autosize>
      <Group justify="space-between" mt="md">
        <Button variant="subtle" onClick={() => ir(`${RUTAS.ayuda}?seccion=novedades`)}>Historial completo en Ayuda</Button>
        <Button onClick={cerrar}>Entendido</Button>
      </Group>
    </Modal>
  );
}
